import type { Expense } from '@split/shared';
import { del, get, keys, set } from 'idb-keyval';

/** Same name as the service worker's cache-first route for receipt images (`sw.ts`). */
const SW_CACHE = 'receipts';
const QUEUE_PREFIX = 'split.receipt.';

/**
 * Receipt image URL for `<img>`. The API URL does not change when the receipt is replaced, so it
 * is versioned with `updatedAt` (bumped by every upload/removal): the service worker caches it
 * cache-first and must never serve an older image for a newer expense.
 */
export function receiptSrc(e: Pick<Expense, 'receipt' | 'updatedAt'>): string | null {
  return e.receipt ? `${e.receipt.url}?v=${encodeURIComponent(e.updatedAt)}` : null;
}

/** Drops every cached version of an expense's receipt (after a replace or removal). */
export async function evictReceipt(expenseId: string): Promise<void> {
  if (typeof caches === 'undefined') return;
  try {
    const cache = await caches.open(SW_CACHE);
    await cache.delete(`/api/expenses/${expenseId}/receipt`, { ignoreSearch: true });
  } catch {
    // Cache Storage unavailable (private mode…): nothing cached either.
  }
}

/** Deletes the service worker's receipt cache (sign out / account switch). */
export async function clearReceiptCache(): Promise<void> {
  if (typeof caches === 'undefined') return;
  try {
    await caches.delete(SW_CACHE);
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Receipts of queued (offline) expense mutations. Mutation variables are persisted as JSON, which
// turns a Blob into `{}`, so the photo is kept in IndexedDB under the expense id instead.
// ---------------------------------------------------------------------------

const pending = new Map<string, Blob>();

/** Keeps `blob` until the queued mutation for `expenseId` uploads it. */
export async function queueReceipt(expenseId: string, blob: Blob): Promise<void> {
  pending.set(expenseId, blob);
  try {
    await set(QUEUE_PREFIX + expenseId, blob);
  } catch {
    // No IndexedDB: the receipt survives until a reload only.
  }
}

export async function queuedReceipt(expenseId: string): Promise<Blob | null> {
  const inMemory = pending.get(expenseId);
  if (inMemory) return inMemory;
  try {
    const stored: unknown = await get(QUEUE_PREFIX + expenseId);
    return stored instanceof Blob ? stored : null;
  } catch {
    return null;
  }
}

export async function dropQueuedReceipt(expenseId: string): Promise<void> {
  pending.delete(expenseId);
  try {
    await del(QUEUE_PREFIX + expenseId);
  } catch {
    // ignore
  }
}

/** Expense ids with a queued photo in IndexedDB. */
export async function queuedReceiptIds(): Promise<string[]> {
  try {
    const all = await keys();
    return all
      .filter((k): k is string => typeof k === 'string' && k.startsWith(QUEUE_PREFIX))
      .map((k) => k.slice(QUEUE_PREFIX.length));
  } catch {
    return [];
  }
}

export async function clearQueuedReceipts(): Promise<void> {
  pending.clear();
  try {
    const all = await keys();
    await Promise.all(
      all.filter((k) => typeof k === 'string' && k.startsWith(QUEUE_PREFIX)).map((k) => del(k)),
    );
  } catch {
    // ignore
  }
}
